import { useState } from 'react'
import { useNavigate } from 'react-router'

import type { RecipeListRow } from '@ctcells/contract'
import { PATHS } from '@ctcells/core'
import { RECIPE_LIST } from '@ctcells/fixtures'
import { intlLocale, recipeStatusLabel, t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { DataTable, type Column } from '../../atoms/DataTable.tsx'
import { SearchInput } from '../../atoms/SearchInput.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { recipeBadgeFlat, recipeStatusTone } from '../../atoms/statusTone.ts'
import type { TableSort } from '../../atoms/tableSort.ts'
import { Toolbar } from '../../atoms/Toolbar.tsx'
import { ListPage } from '../../layout/ListPage.tsx'
import { useGroupTabLayout } from '../groupTabs.ts'
import { RESOURCE_TABS, type ResourceTabId } from '../resources/resourceTabs.ts'
import { gate, type ScreenAccess } from '../access.ts'
import { OfflineNotice, RowLink } from '../resourceParts.tsx'
import { withPriority } from '../listColumnPriority.ts'
import { fullDateTimeText } from '../format.ts'
import { moduleStepChips } from './recipeForm.ts'
import { StepCountChips } from './RecipeTables.tsx'
import { ownerSearchTexts, ownerValue, type BatchOwners } from '../batches/batchOwnerColumn.tsx'

import '../screens.css'
import '../resource.css'

/**
 * 레시피 목록 — 와이어프레임 `recipe()` 2077행.
 *
 * 🔴 **필터 탭을 두지 않는다.** 초안·확정·보관으로 목록을 가르지 않고 한 목록 + 행 배지다
 *    (eric 지시 2026-09-15 · §1-4). 탭이 하나뿐이면 탭 줄 자체가 없으므로(`Tabs` 가 집행)
 *    주 버튼은 검색 툴바 오른쪽 끝으로 간다 — 자리는 `ListPage` 가 쥔다.
 *
 * 🔴 정렬 라벨을 「초안 → 커밋 → 보관」에서 **「작성 중 → 확정 → 보관」**으로 바로잡았다.
 *    와이어프레임 2085행이 확정 표기를 안 따르고 있었다(README §19-6).
 */
export interface RecipesScreenProps {
  /**
   * 열린 「준비」 탭. 🔴 **라우트가 넘긴다** — 탭이 곧 주소다(`resources/resourceTabs.ts`).
   * **안 넘기면 무리 탭 줄을 안 그린다**(규칙은 `useGroupTabLayout` 주석) — 장비 관점 콘솔이
   * 그렇게 쓴다. 거기선 시료·레시피·랩웨어가 사이드바에 각자 서 있어서 탭 줄이 또 뜨면 안 된다.
   */
  resourceTab?: ResourceTabId | undefined
  recipes?: readonly RecipeListRow[]
  /** 오른쪽 판에 열린 레시피. 그 행이 «고른 행»이다(채움 + `aria-selected`) */
  selectedRecipeId?: string | undefined
  access?: ScreenAccess
  loading?: boolean
  /**
   * 「작업자」 이름표 — 🔴 **admin 에게만 값이 있다**(eric 2026-09-30 「작업자랑 등록 이렇게 두 열은 붙여줘」 ·
   * 작업 목록과 같은 규칙 · `useBatchOwners`). 안 넘기면 「작업자」 열 자체가 없고 `GET /users` 도 안 나간다.
   */
  owners?: BatchOwners | undefined
}

const STATUS_ORDER: Record<RecipeListRow['status'], number> = {
  draft: 0,
  committed: 1,
  archived: 2,
}

/**
 * 🔴 **정렬은 머리글이다**(eric 2026-09-30 「게시판 테이블의 컬럼 헤더 클릭하면.. 내림 차순 정렬.. 한번 더 클릭하면 오름
 *    차순」). 종전의 정렬 알약(최근 등록 · 이름 · 버전 높은 순 · 상태)은 걷었다 — 두 정렬이 한 표에서 서로를 덮는다.
 *    머리글은 레시피 · 상태 · 단계 · 작업자(admin) · 등록이다(버전 열은 2026-09-30 에 뺐다). 처음 차례는 등록 내림차순(최신 위).
 */
const RECIPE_DEFAULT_SORT: TableSort = { key: 'created', direction: 'desc' }

/**
 * 레시피 상세로 가는 곳. 🔴 **첫 칸 링크 · 행 클릭이 같은 함수를 쓴다** —
 * 경로 문자열을 여러 벌 적으면 한쪽만 고쳐져 누르는 자리마다 다른 데로 간다.
 */
const recipeDetailPath = (recipe: RecipeListRow): string => `${PATHS.recipes}/${recipe.recipe_id}`

export function RecipesScreen({
  resourceTab,
  recipes = RECIPE_LIST,
  access = {},
  loading = false,
  selectedRecipeId,
  owners,
}: RecipesScreenProps) {
  /*
   * 🔴 「준비」 무리의 탭 줄.
   * ⚠ 레시피는 **화면 자체 탭이 없다** — 시료·랩웨어와 달리 하위 줄이 없고, 무리 탭을 끄면
   *   탭 줄 자체가 사라진다(`v0.1.0` 의 모양 그대로다). 주 버튼은 그대로 머리줄에 있다.
   */
  const groupTabs = useGroupTabLayout(RESOURCE_TABS, resourceTab, 'recipes', access)
  const navigate = useNavigate()
  const newRecipeGate = gate(access, { perm: 'control' })
  const [query, setQuery] = useState('')

  const needle = query.trim().toLowerCase()
  /*
   * 🔴 검색은 이름 · (admin 이면) 작업자 이름 · 계정명이다 — 계정 ID 로는 걸지 않는다(작업 목록과 같은 규칙 ·
   *    `ownerSearchTexts`). 종전에는 모두에게 `created_by`(계정 ID)로 걸렸다 — 화면에 없는 값으로 찾는 셈이었다.
   */
  const rows = recipes.filter(
    (recipe) =>
      needle === '' ||
      [recipe.name, ...ownerSearchTexts(owners, recipe.created_by)].some((v) =>
        v.toLowerCase().includes(needle),
      ),
  )

  /*
   * 🔴 **집계 경로가 없어 뺀 열이다**(2026-09-20). 되살리지 마라 — 서버가 그 값을 «만들지 못한다».
   *    되살리려면 먼저 API 가 있어야 한다. `—`·`0` 으로 채운 열을 깔아두는 것이 이번에 걷어낸 병이다.
   */
  const columns: Column<RecipeListRow>[] = [
    {
      key: 'name',
      // 🔸 목록 첫 열 머리글은 「이름」(eric 2026-09-30 — 준비 세 목록 공통)
      header: t('column.name'),
      /*
       * 🔴 **「설명」 아랫줄을 되살리지 마라**(2026-09-21 제거). `Recipe` 에 `description` 이
       *    «없다»(실측 17속성 전수 · `RecipeWriteEnvelope` 에도 없다). 종전에는 변환이
       *    `description: t('mask.none')` 을 «박아» 넣어서 **모든 행의 아랫줄이 언제나 `—`** 였다.
       *    「응답에 없는 값이면 그 자리를 만들지 않는다」(eric 확정) — `usage_count` 와 같은 축이다.
       */
      sort: 'text',
      sortValue: (recipe) => recipe.name,
      cell: (recipe) => <RowLink to={recipeDetailPath(recipe)}>{recipe.name}</RowLink>,
    },
    {
      key: 'status',
      header: t('column.status'),
      nowrap: true,
      // 작성 중 → 확정 → 보관이 오름차순이다
      sort: (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status],
      cell: (recipe) => (
        <StatusBadge
          /* 바탕 있는 칩은 테 없는 알약(eric 2026-10-30 「여기 확정도.. 테두리 없애줘」) · 색 · 글자 그대로 */
          flat={recipeBadgeFlat(recipe.status, recipeStatusTone(recipe.status))}
          label={recipeStatusLabel(recipe.status)}
          status={recipe.status}
          tone={recipeStatusTone(recipe.status)}
        />
      ),
    },
    /*
     * 🔴 **「버전」 열은 없다**(eric 2026-09-30 「레시피 테이블에서.. 버전은 빼줘」). 값은 서랍 · 상세의 부제 줄
     *    (`v3 · 스키마`)이 말한다. 이름 옆 `v1` 표기(작업 추가 창 등)는 별개다 — 여기는 목록 표의 열만이다.
     */
    {
      key: 'steps',
      header: t('column.step'),
      /* 오른쪽 판이 열려 표가 좁아져도 칩 셋(원심 · 어셈블 · 염색)이 한 줄이다 — 세로로 부서지면 못 읽는다 */
      nowrap: true,
      // 스텝 합계로 센다(세 모듈의 합 — 요약 카드 「스텝」 줄의 첫 수와 같다)
      sort: 'number',
      // 🔴 새 형식은 단위가 다르다(명령) — 정렬에서 맨 뒤로(값 없음)
      sortValue: (recipe) =>
        recipe.new_format_commands !== undefined
          ? null
          : recipe.step_counts.centrifuge +
            recipe.step_counts.assembler +
            recipe.step_counts.staining,
      /*
       * 🔴 칩 모양은 디자인팀 시안 `.steps3`(2026-10-12 · eric 「원심 어셈블 염색.. 이거 표현하는 거 디자인팀에서.. 제공한 디자인
       *    적용해줘」). 칩은 옛 형식에만 선다 — 수는 응답 `payload` 의 모듈별 `steps` 길이이고, 시안의 수(57 · 101 · 71)를 박지 않는다.
       *    새 형식은 장비별 수가 응답에 없어 종전 「명령 N」 글자다.
       */
      cell: (recipe) =>
        recipe.new_format_commands !== undefined ? (
          /* 🔴 새 형식 — 옛 절이 없어 0 이 되는 모듈별 스텝 대신 문서의 명령 수(종전 글자 그대로 —
             장비별 수가 응답에 없어 칩을 짓지 않는다 · 팀장 2026-10-12), 없으면 빈칸 + 사유 */
          recipe.new_format_commands === null ? (
            <span className="muted" title={t('reason.v2NoCommands')}>
              {t('mask.none')}
              <span className="a-sr">{t('reason.v2NoCommands')}</span>
            </span>
          ) : (
            <span className="muted">
              {t('recipeV2.commandsCell', {
                count: recipe.new_format_commands.toLocaleString(intlLocale()),
              })}
            </span>
          )
        ) : (
          <StepCountChips chips={moduleStepChips(recipe.step_counts)} />
        ),
    },
    /*
     * 🔴 **「작업자」 — admin 에게만**(eric 2026-09-30 「작업자랑 등록 이렇게 두 열은 붙여줘」). 작업 목록과 같은 규칙이다:
     *    응답 `Recipe.created_by` 는 계정 ID 라 이름은 admin 전용 `GET /users` 로만 풀린다(`useBatchOwners`).
     *    operator · viewer 에게는 열 자체가 없다(ID 도 안 샌다). 이름으로 세운다 — 못 푼 id · `null` 은 맨 뒤다.
     */
    ...(owners === undefined
      ? []
      : [
          {
            key: 'owner',
            header: t('column.owner'),
            nowrap: true,
            sort: 'text',
            sortValue: (recipe: RecipeListRow) =>
              recipe.created_by === null ? null : owners.byId.get(recipe.created_by)?.name,
            cell: (recipe: RecipeListRow) => ownerValue(owners, recipe.created_by),
          } satisfies Column<RecipeListRow>,
        ]),
    {
      /*
       * 🔴 **「등록」이다**(eric 2026-09-30 「등록 시각이라고 되어 있는 건.. 등록이라고만 해줘」). 값은 연-월-일 시:분:초
       *    전부(시료 목록 · 알람과 같다). 종전 「생성」 칸이 붙여 적던 작성자는 「작업자」 열(admin)로 갈라 나갔다.
       * ⚠ `Recipe` 에 `updated_at` 이 «없다» — 「수정」 열로 되돌리지 마라(재커밋으로 version 이 오르는 모델이다).
       */
      key: 'created',
      header: t('column.registered'),
      nowrap: true,
      sort: 'time',
      sortValue: (recipe) => recipe.created_at,
      cell: (recipe) => <span className="muted">{fullDateTimeText(recipe.created_at)}</span>,
    },
    /*
     * 🔴 **행 메뉴(`⋯`) 열은 없다**(eric 2026-09-30 「레시피에 맨 마지막에.. ... 이 컬럼.. 빼줘」). 그 메뉴의 둘은
     *    다른 자리에 이미 있다 — 「상세 열기」= 행 클릭 · 첫 칸 링크, 「이 레시피로 작업 만들기」= 서랍 머리의 같은 단추
     *    (`RecipeDetailScreen` · 비활성 사유까지 같다). 되살리지 마라.
     */
  ]

  return (
    <ListPage
      {...(groupTabs
        ? {
            onTabChange: groupTabs.onChange,
            tabValue: groupTabs.value,
            tabs: groupTabs.tabs,
            tabsLabel: t('screen.resourcesGroup.title'),
          }
        : {})}
      primaryAction={
        /*
          「＋ 레시피」는 단추 하나 — 누르면 서버 표준 레시피를 불러온 새 레시피 창이다(eric 2026-10-12 「+레시피를 클릭하면.. 표준 레시피로
          시작」). 종전 메뉴(빈 레시피 · 표준 레시피에서 · 템플릿 조합으로 · 2026-10-06)는 걷었다.
          🔴 권한이 없으면 비활성 + 사유(숨기지 않는다).
          ⚠ 옆의 [템플릿 관리] 단추는 걷었다 — 템플릿은 작업 준비 탭이다(eric 2026-10-13 「응 그러면. 탭 추가해줘..」)
        */
        <Button
          onClick={() => void navigate(PATHS.recipeNew)}
          size="pill"
          variant="primary"
          {...newRecipeGate}
        >
          {t('action.newRecipe')}
        </Button>
      }
      title={groupTabs ? t('screen.resourcesGroup.title') : t('screen.recipes.title')}
      toolbar={
        <Toolbar label={t('screen.recipes.title')}>
          {/* 눈에 보이는 글자는 「검색」뿐(eric 2026-10-06 · 시료 · 랩웨어와 같다). 찾는 칸은 접근성 이름으로 남는다 */}
          <SearchInput
            label={t(
              owners === undefined ? 'filter.search.recipes' : 'filter.search.recipesWithOwner',
            )}
            onChange={setQuery}
            placeholder={t('filter.search.batchesPlaceholder')}
            value={query}
          />
        </Toolbar>
      }
      /* 검색은 준비 탭 줄 오른쪽 · 주 버튼은 제목 줄 오른쪽(「작업 준비」 공통 · eric 2026-10-06) */
      toolbarInTabs={groupTabs !== null}
    >
      <OfflineNotice shown={access.offline === true} />
      <DataTable
        caption={t('screen.recipes.title')}
        /* 🔴 공간이 모자라면 덜 중요한 열부터 빠진다 — 차례는 `listColumnPriority.ts` 한 곳 */
        columns={withPriority('recipes', columns)}
        defaultSort={RECIPE_DEFAULT_SORT}
        emptyLabel={t('state.empty.recipeList')}
        loading={loading}
        /*
         * 🔴 **행 아무 칸이나 눌러도 열린다**(eric 2026-09-23). 첫 칸 `RowLink` 는 그대로다.
         */
        onRowClick={(recipe) => void navigate(recipeDetailPath(recipe))}
        rowKey={(recipe) => recipe.recipe_id}
        rows={rows}
        selected={(recipe) => recipe.recipe_id === selectedRecipeId}
      />
    </ListPage>
  )
}
